import { Text, Anchor, Title, Stack } from "@mantine/core"
import React from "react"

import {
  CUSTOMISATION_CATEGORIES,
  TEXT_CONTENT_TYPE,
} from "@tfg/types/constants/communication-studio.constant"
import {
  ComponentContent,
  FormattedTextContent,
  Formatting,
  FormattedTextContentSchema,
} from "@tfg/types/schemas/communication-studio/components"
import { TextTransform } from "@tfg/types/schemas/communication-studio/cssStylesSchema"
import {
  headerFontWeightCSSVariable,
  headerLineHeightCSSVariable,
  headerTextDecorationCSSVariable,
  headerFontFamilyCSSVariable,
  textColorCssVariable,
  textFontSizeCSSVariable,
  textFontWeightCSSVariable,
  titleColorCSSVariable,
} from "@tfg/types/schemas/communication-studio/cssVariables"
import { StylingParameters } from "@tfg/types/types/communication-studio/styling-parameters.types"

import { useGetStylingParametersContext } from "../context/stylingParameters/useGetStylingParametersContext"
import { DesignStudioParametersToolkit } from "../utils/designStudioParametersToolkit"

import { useGetContentInLanguage } from "./TextParagraph"

export interface TextParagraphV2RendererProps {
  content: ComponentContent
  className?: string
}

export const TextParagraphV2Renderer = ({
  content,
}: TextParagraphV2RendererProps) => {
  const contentInLanguage = useGetContentInLanguage(content)
  if (!contentInLanguage || contentInLanguage.length === 0) {
    return null
  }
  const formattedTextContent = FormattedTextContentSchema.array().safeParse(
    contentInLanguage[0].textContent ?? {}
  )
  if (!formattedTextContent.success) {
    console.error("formattedTextContent", formattedTextContent.error)
    return null
  }
  return (
    <RenderFormattedText formattedTextContent={formattedTextContent.data} />
  )
}

export function RenderFormattedText({
  formattedTextContent,
}: {
  formattedTextContent: FormattedTextContent[]
}) {
  const { stylingParameters } = useGetStylingParametersContext()

  const groupedElements: React.ReactNode[] = []
  let currentParagraphGroup: React.ReactNode[] = []

  const flushParagraphGroup = () => {
    if (currentParagraphGroup.length > 0) {
      groupedElements.push(
        <Stack
          gap="xs"
          key={`paragraph-group-${groupedElements.length}`}
          style={DesignStudioParametersToolkit.spreadStylingParameters({
            category: CUSTOMISATION_CATEGORIES.GLOBAL,
            stylingParameters,
            stylingParamKeys: ["lineHeight"],
          })}
        >
          {currentParagraphGroup}
        </Stack>
      )
      currentParagraphGroup = []
    }
  }

  for (const [index, content] of formattedTextContent.entries()) {
    const { text, formatting = [] } = content

    if (!text) {
      continue
    }

    if (content.tag === "h3") {
      flushParagraphGroup()

      const headingBlocks = renderBlocks({
        text,
        formatting,
        tag: content.tag,
        stylingParameters,
        index,
      })
      groupedElements.push(...headingBlocks)
    } else {
      if (formatting.length === 0) {
        currentParagraphGroup.push(
          <Text
            key={`para-${index}`}
            component="p"
            my={0}
            style={DesignStudioParametersToolkit.spreadStylingParameters({
              category: CUSTOMISATION_CATEGORIES.GLOBAL,
              stylingParameters,
              stylingParamKeys: ["letterSpacing"],
            })}
            fz={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontSizeCSSVariable
            )}
            fw={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontWeightCSSVariable
            )}
            c={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textColorCssVariable
            )}
          >
            {text}
          </Text>
        )
      } else {
        const paragraphBlocks = renderBlocks({
          text,
          formatting,
          tag: content.tag,
          stylingParameters,
          index,
        })
        currentParagraphGroup.push(...paragraphBlocks)
      }
    }
  }

  flushParagraphGroup()

  return <>{groupedElements}</>
}

function renderBlocks({
  text,
  tag,
  formatting,
  stylingParameters,
  index,
}: {
  text: string
  tag: "p" | "h3"
  formatting: Formatting
  stylingParameters: StylingParameters
  index: number
}): React.ReactNode[] {
  const sortedFormatting =
    tag === "p" ? [...formatting].sort((a, b) => a.start - b.start) : []
  const blocks: React.ReactNode[] = []
  let inlineBuffer: React.ReactNode[] = []
  let lastIndex = 0
  let keyCounter = 0
  function flushBuffer() {
    if (inlineBuffer.length > 0) {
      if (tag === "p") {
        blocks.push(
          <Text
            key={`p-${index}`}
            style={DesignStudioParametersToolkit.spreadStylingParameters({
              category: CUSTOMISATION_CATEGORIES.GLOBAL,
              stylingParameters,
              stylingParamKeys: ["letterSpacing"],
            })}
            fz={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontSizeCSSVariable
            )}
            fw={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontWeightCSSVariable
            )}
            c={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textColorCssVariable
            )}
          >
            {inlineBuffer}
          </Text>
        )
      } else if (tag === "h3") {
        blocks.push(
          <Title
            key={`h3-${index}`}
            order={3}
            style={DesignStudioParametersToolkit.spreadStylingParameters({
              category: CUSTOMISATION_CATEGORIES.GLOBAL,
              stylingParameters,
              stylingParamKeys: ["letterSpacing", "lineHeight"],
            })}
            td={DesignStudioParametersToolkit.getCssVariableLiteralName(
              headerTextDecorationCSSVariable
            )}
            fw={DesignStudioParametersToolkit.getCssVariableLiteralName(
              headerFontWeightCSSVariable
            )}
            ff={`${DesignStudioParametersToolkit.getCssVariableLiteralName(
              headerFontFamilyCSSVariable
            )}, ${DesignStudioParametersToolkit.extractFontFamily(stylingParameters)}
           `}
            tt={
              DesignStudioParametersToolkit.extractValueFromCssVariable(
                stylingParameters,
                CUSTOMISATION_CATEGORIES.GLOBAL,
                "headerTextTransform"
              ) as TextTransform | undefined
            }
            lh={DesignStudioParametersToolkit.getCssVariableLiteralName(
              headerLineHeightCSSVariable
            )}
            c={DesignStudioParametersToolkit.getCssVariableLiteralName(
              titleColorCSSVariable
            )}
          >
            {inlineBuffer}
          </Title>
        )
      }
      inlineBuffer = []
    }
  }

  sortedFormatting.forEach((format) => {
    if (format.start > lastIndex) {
      const plainText = text.slice(lastIndex, format.start)
      if (plainText) {
        inlineBuffer.push(plainText)
      }
    }
    const key = `format-${keyCounter++}`

    switch (format.type) {
      case TEXT_CONTENT_TYPE.STRONG: {
        const boldText = text.slice(format.start, format.end)
        inlineBuffer.push(<strong key={key}>{boldText}</strong>)
        lastIndex = format.end
        break
      }

      case TEXT_CONTENT_TYPE.HYPERLINK: {
        const hyperlinkText = text.slice(format.start, format.end)
        inlineBuffer.push(
          <Anchor
            key={key}
            href={format.link}
            target="_blank"
            rel="noreferrer"
            td="underline"
            fz={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontSizeCSSVariable
            )}
            fw={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontWeightCSSVariable
            )}
            c={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textColorCssVariable
            )}
          >
            {hyperlinkText}
          </Anchor>
        )
        lastIndex = format.end
        break
      }

      default: {
        const unknownText = text.slice(format.start, format.end)
        inlineBuffer.push(<span key={key}>{unknownText}</span>)
        lastIndex = format.end
        break
      }
    }
  })

  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex)
    if (remainingText) {
      inlineBuffer.push(remainingText)
    }
  }
  flushBuffer()
  return blocks
}

export default TextParagraphV2Renderer
